// frontend/src/App.js
// ═══════════════════════════════════════════════════════════════
// VERSIÓN CON CENTRO DE SOPORTE
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

// Componentes existentes
import UserLogin from './UserLogin';
import UserDashboard from './UserDashboard';
import AdminDashboard from './AdminDashboard';
import SuperAdminDashboard from './superadmin/SuperAdminDashboard';
import NavigationBar from './navbar';
import Home from './Home';
import Contactos from './Contactos';
import Recursos from './Recursos';
import AdminAlicuotas from './alicuotas/AdminAlicuotas';
import Register from './auth/Register';
import PricingPlans from './billing/PricingPlans';
import PaymentCheckout from './billing/PaymentCheckout';
import ForgotPassword from './auth/ForgotPassword';
import ResetPassword from './auth/ResetPassword';
import AccountSuspended from './pages/AccountSuspended';
import SubscriptionExpiredPopup from './billing/SubscriptionExpiredPopup';
import { API_ENDPOINTS } from './config/api';

// ═══════════════════════════════════════════════════════════════
// ✅ NUEVO: Centro de Soporte
// ═══════════════════════════════════════════════════════════════
import AdminSupport from './AdminSupport';

import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Detectar si estamos en el panel SuperAdmin (layout independiente)
  const isSuperAdminRoute = location.pathname.startsWith('/superadmin');

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
      checkAccountStatus(storedUser);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // Verificar estado de cuenta + suscripcion expirada
  // ═══════════════════════════════════════════════════════════════
  const checkAccountStatus = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // 1) Verificar account_status del servidor
      const response = await fetch(API_ENDPOINTS.accountStatus, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.data) {
          if (data.data.is_suspended) {
            const updatedUser = { ...userData, account_status: 'suspended' };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            return;
          }
          // Si el servidor dice pending_payment, sincronizar
          if (data.data.account_status === 'pending_payment') {
            const updatedUser = { ...userData, account_status: 'pending_payment' };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            return;
          }
        }
      }

      // 2) Para Admins: verificar si la suscripcion esta expirada via billing
      if (userData.role === 'Admin') {
        try {
          const billingRes = await fetch(API_ENDPOINTS.billingInfo, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (billingRes.ok) {
            const billingData = await billingRes.json();
            const sub = billingData?.data?.subscription;
            if (sub && sub.days_remaining <= 0 && !sub.is_active) {
              console.warn('Suscripcion expirada detectada - activando popup de renovacion');
              const updatedUser = { ...userData, account_status: 'pending_payment' };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }
          }
        } catch (billingErr) {
          console.log('Info: No se pudo verificar billing info');
        }
      }
    } catch (error) {
      console.log('Info: Endpoint /api/account/status no disponible');
    }
  };

  const handleLogin = (loggedInUser) => {
    setUser(loggedInUser);
    localStorage.setItem('user', JSON.stringify(loggedInUser));
  };

  const handleLogout = () => {
    localStorage.clear();
    setUser(null);
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  // Actualizar usuario después de pago exitoso
  const handlePaymentSuccess = (paymentDetails) => {
    console.log('💳 Pago exitoso:', paymentDetails);
    
    // Actualizar estado del usuario si es necesario
    if (paymentDetails && paymentDetails.accountStatus) {
      const updatedUser = { 
        ...user, 
        account_status: paymentDetails.accountStatus,
        paypal_email: paymentDetails.payerEmail
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const getDashboard = () => {
    if (!user) return <Navigate to="/login" replace />;

    // Validar account_status - Admin con suscripcion vencida ve popup de renovacion
    if (user.account_status === 'pending_payment' && user.role === 'Admin') {
      return <SubscriptionExpiredPopup user={user} onLogout={handleLogout} />;
    }

    if (user.account_status === 'suspended' || user.account_status === 'cancelled') {
      return <Navigate to="/account-suspended" replace />;
    }

    switch(user.role) {
      case 'SuperAdmin':
        // Redirigir al nuevo panel SuperAdmin Pro
        return <Navigate to="/superadmin" replace />;
      case 'Admin':
        return <AdminDashboard user={user} onLogout={handleLogout} onPaymentSuccess={handlePaymentSuccess} />;
      case 'User':
      default:
        return <UserDashboard user={user} onLogout={handleLogout} onPaymentSuccess={handlePaymentSuccess} />;
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  // Si es ruta de SuperAdmin, renderizar sin navbar publico
  if (isSuperAdminRoute) {
    return (
      <div className="App superadmin-fullscreen">
        <Routes>
          <Route
            path="/superadmin/*"
            element={
              user && user.role === 'SuperAdmin'
                ? <SuperAdminDashboard user={user} onLogout={handleLogout} />
                : <Navigate to="/login" replace />
            }
          />
          <Route path="*" element={<Navigate to="/superadmin" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <div className="App">
      <NavigationBar user={user} onLogout={handleLogout} />
      <Routes>
        {/* Login */}
        <Route 
          path="/login" 
          element={!user ? <UserLogin onLogin={handleLogin} /> : <Navigate to="/dashboard" replace />}
        />

        {/* Registro */}
        <Route 
          path="/register" 
          element={!user ? <Register /> : <Navigate to="/dashboard" replace />}
        />

        {/* Recuperación de Contraseña */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Cuenta Suspendida */}
        <Route 
          path="/account-suspended" 
          element={
            user && (user.account_status === 'suspended' || user.account_status === 'cancelled') 
              ? <AccountSuspended /> 
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* Planes y Pago */}
        <Route path="/planes" element={<PricingPlans />} />
        <Route path="/billing/checkout" element={<PaymentCheckout />} />

        {/* Dashboard */}
        <Route path="/dashboard" element={getDashboard()} />

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* ✅ SuperAdmin Panel Pro - UrbanQ v1.8.9.4.5.7 */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <Route
          path="/superadmin/*"
          element={
            user && user.role === 'SuperAdmin'
              ? <SuperAdminDashboard user={user} onLogout={handleLogout} />
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* Alícuotas (Admin) */}
        <Route 
          path="/alicuotas" 
          element={
            user && user.role === 'Admin' && 
            user.account_status !== 'pending_payment' && 
            user.account_status !== 'suspended' && 
            user.account_status !== 'cancelled'
              ? <AdminAlicuotas user={user} /> 
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* ✅ NUEVO: Centro de Soporte (Admin y SuperAdmin) */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <Route 
          path="/soporte" 
          element={
            user && (user.role === 'Admin' || user.role === 'SuperAdmin') &&
            user.account_status !== 'pending_payment' && 
            user.account_status !== 'suspended' && 
            user.account_status !== 'cancelled'
              ? <AdminSupport user={user} /> 
              : <Navigate to="/dashboard" replace />
          }
        />

        {/* Home, Contactos y Recursos */}
        <Route path="/" element={<Home />} />
        <Route path="/contactos" element={<Contactos />} />
        <Route path="/recursos" element={<Recursos />} />

        {/* Ruta por defecto */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
